import { useMemo, useState } from 'react';
import { Form, Spin } from 'antd';
import styled from 'styled-components';
import aptSelectors from 'services/selectors/apt';
import aptActions from 'services/actions/apt';
import { useAppDispatch } from 'store/hooks';
import { ICallerCallItem, ICallerDetail, ICallerHistoryEntry } from '../types';
import { ICustomerUI } from 'types/customer';
import { Body, Scroll } from './detail-styles';
import CustomerSection from './CustomerSection';
import HistorySection from './HistorySection';
import BookingOrderSection from './BookingOrderSection';
import BookingHistoryModal from './BookingHistoryModal';
import DetailFooter from './DetailFooter';
import { mapApptItemToHistoryEntry, DEFAULT_PAGE_SIZE } from '../mappers';
import useBookingComposer from './useBookingComposer';

// Caller Detail panel (Figma node 19:4515). Reads the active caller from the
// navigator form (set when BOOK is pressed on a list card) and renders the
// customer / history / notes sections + a functional Booking-Order composer.
const CallerDetailTab = () => {
  const navigatorForm = Form.useFormInstance();
  const item = Form.useWatch('activeItem', navigatorForm) as ICallerCallItem | undefined;
  const onBack = () => navigatorForm.resetFields();

  // Reflect a customer edit back into the active caller so the panel re-renders.
  const onCustomerUpdated = (customer: ICustomerUI) => {
    if (!item) return;
    navigatorForm.setFieldValue('activeItem', { ...item, name: customer.name, phone: customer.phone });
  };

  if (!item) return null;
  return <CallerDetailContent item={item} onBack={onBack} onCustomerUpdated={onCustomerUpdated} />;
};

// Inner component so the composer hooks run unconditionally once a caller exists.
const CallerDetailContent = ({ item, onBack, onCustomerUpdated }: {
  item: ICallerCallItem;
  onBack: () => void;
  onCustomerUpdated: (customer: ICustomerUI) => void;
}) => {
  const dispatch = useAppDispatch();
  const composer = useBookingComposer(item);
  const [historyOpen, setHistoryOpen] = useState(false);
  const recent = aptSelectors.customerRecent.data();
  const recentLoading = aptSelectors.customerRecent.loading();

  // Full paginated appointment history for the "See more" modal. Only trust the
  // slice for callers with a customerId — a "New" caller skips the fetch, so the
  // slice may still hold a previously-booked customer's page.
  const hasCustomer = !!item.customerId;
  const apptItems = aptSelectors.customerAppts.data();
  const apptTotalRaw = aptSelectors.customerAppts.total();
  const apptPage = aptSelectors.customerAppts.page();
  const apptPageSize = aptSelectors.customerAppts.pageSize();
  const apptLoading = aptSelectors.customerAppts.loading();
  const apptTotal = hasCustomer ? apptTotalRaw : 0;
  const fullHistory = useMemo(
    () => (hasCustomer ? apptItems.map(mapApptItemToHistoryEntry) : []),
    [apptItems, hasCustomer],
  );

  const fetchApptPage = (page: number) => {
    if (!item.customerId) return;
    dispatch(aptActions.getCustomerAppointments.fetch({
      customerId: Number(item.customerId),
      page,
      pageSize: DEFAULT_PAGE_SIZE,
    }));
  };

  const openHistory = () => {
    setHistoryOpen(true);
    fetchApptPage(1);
  };

  // Only trust recent data that belongs to the active caller — guards against a
  // "New" caller (no customerId) or stale data from a previously booked caller.
  const detail: ICallerDetail = useMemo(() => {
    const matches = !!recent && !!item.customerId && String(recent.customer.id) === item.customerId;
    const history = recent && matches ? recent.recentAppointments.map(mapApptItemToHistoryEntry) : [];
    return { history, notes: [] };
  }, [recent, item.customerId]);

  const handleBook = () => {
    if (!composer.canBook) return;
    composer.submit();
    onBack(); // success UX: close detail → back to list (saga shows toast + refresh)
  };

  // Re Book (history section or modal) → prefill the composer services, close the modal.
  const handleRebook = (entry: ICallerHistoryEntry) => {
    composer.prefillServices(entry.services);
    setHistoryOpen(false);
  };

  return (
    <>
      <Body>
        <Scroll>
          <CustomerSection item={item} onCustomerUpdated={onCustomerUpdated} />
          {recentLoading ? (
            <HistoryLoading><Spin /></HistoryLoading>
          ) : detail.history.length > 0 ? (
            <HistorySection
              detail={detail}
              onSeeMore={openHistory}
              onRebook={handleRebook}
            />
          ) : null}
          <BookingOrderSection composer={composer} />
        </Scroll>
        <DetailFooter
          onBack={onBack}
          onSms={composer.onSms}
          onBook={handleBook}
          bookDisabled={!composer.canBook}
        />
      </Body>
      <BookingHistoryModal
        open={historyOpen}
        item={item}
        history={fullHistory}
        total={apptTotal}
        page={apptPage}
        pageSize={apptPageSize}
        loading={apptLoading}
        onPageChange={fetchApptPage}
        onClose={() => setHistoryOpen(false)}
        onRebook={handleRebook}
      />
    </>
  );
};

const HistoryLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

export default CallerDetailTab;
